import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Play } from "lucide-react";
import { API } from "@/lib/api";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import ArticleCard from "@/components/ArticleCard";
import Scoreboard from "@/components/Scoreboard";
import TikTokEmbed, { TikTokEmbedLoader } from "@/components/TikTokEmbed";
import NewsletterSection from "@/components/NewsletterSection";
import SportingMatch from "@/components/SportingMatch";

const HERO_IMG = "/estadio-molinon.jpg";

const HeroLine = ({ children, index }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay: 0.2 + index * 0.13, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const TiltCard = ({ article }) => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 160, damping: 18 });
  const sry = useSpring(ry, { stiffness: 160, damping: 18 });
  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 14);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };
  if (!article) return null;
  return (
    <motion.div
      ref={ref}
      data-testid="hero-featured-cronica"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d", transformPerspective: 900 }}
      className="w-full max-w-md"
    >
      <Link to={`/articulo/${article.slug}`} className="block">
        <div className="overflow-hidden rounded-2xl border border-white/15 bg-ink2/90 shadow-[0_32px_80px_-24px_rgba(230,0,38,0.35)] backdrop-blur-xl">
          <div className="stripes-rojiblancas h-1.5 w-full" />
          <div className="p-6" style={{ transform: "translateZ(40px)" }}>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-2 w-2 animate-pulse-dot rounded-full bg-glow" />
              <span className="font-mono-score text-[10px] font-bold uppercase tracking-[0.25em] text-glow">
                Última crónica
              </span>
            </div>
            <p className="font-mono-score text-[11px] uppercase tracking-widest text-slate-400">
              {article.competition}
            </p>
            <h3 className="mt-2 font-display text-2xl font-extrabold uppercase leading-tight text-white">
              {article.home_team} <span className="font-mono-score text-crimson">{article.home_score}:{article.away_score}</span> {article.away_team}
            </h3>
            <p className="mt-3 line-clamp-2 text-sm text-slate-400">{article.excerpt}</p>
            <span className="mt-4 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-crimson">
              Leer crónica <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const Home = () => {
  const [articles, setArticles] = useState([]);
  const [tiktokUrls, setTiktokUrls] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);

  useEffect(() => {
    API.get("/articles", { params: { limit: 12 } }).then(({ data }) => setArticles(data));
    API.get("/tiktok/videos").then(({ data }) => setTiktokUrls(data.urls || [])).catch(() => {});
  }, []);

  const featuredCronica = articles.find((a) => a.category === "cronica");
  const featured = articles.filter((a) => a.featured && a !== featuredCronica);
  const latest = articles.filter((a) => a.category === "articulo");
  const cronicas = articles.filter((a) => a.category === "cronica");
  const bento = [...featured, ...latest].slice(0, 3);

  return (
    <div className="bg-ink">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden pt-28">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Estadio rojiblanco" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-transparent" />
        </motion.div>
        <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 px-5 pb-16 md:px-12 lg:grid-cols-[1.4fr_1fr] lg:items-end">
          <div>
            <motion.img
              src="/logo.png"
              alt="Escudo ENOLFC"
              data-testid="hero-logo"
              initial={{ opacity: 0, scale: 0.5, rotate: -12 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 120, damping: 14, delay: 0.05 }}
              className="mb-6 h-24 w-24 rounded-full shadow-[0_0_60px_-8px_rgba(230,0,38,0.8)] ring-2 ring-crimson/70 sm:h-32 sm:w-32"
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.7 }}
              className="eyebrow mb-5"
            >
              ENOLFC · Periodismo rojiblanco desde la grada
            </motion.p>
            <h1 className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-8xl">
              <HeroLine index={0}>El fútbol se</HeroLine>
              <HeroLine index={1}>vive en</HeroLine>
              <HeroLine index={2}>
                <span className="text-crimson text-glow-red">rojo y blanco</span>
              </HeroLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.7 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            >
              Artículos, crónicas de cada partido del Sporting de Gijón y análisis sin filtros.
              Todo lo que se siente en El Molinón, contado con el corazón.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/articulos"
                data-testid="hero-cta-articulos"
                className="group flex items-center gap-2 rounded-full bg-crimson px-7 py-3.5 font-display text-lg font-bold uppercase tracking-wider text-white transition-transform hover:scale-105"
              >
                Leer artículos
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="https://www.tiktok.com/@enolfc"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-tiktok"
                className="flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-display text-lg font-bold uppercase tracking-wider text-white backdrop-blur-sm transition-colors hover:border-crimson hover:text-crimson"
              >
                <Play className="h-4 w-4 fill-current" /> TikTok @ENOLFC
              </a>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="hidden justify-end lg:flex"
          >
            <TiltCard article={featuredCronica} />
          </motion.div>
        </div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-white/60"
        >
          <ArrowDown className="h-5 w-5" />
        </motion.div>
      </section>

      <Marquee />

      <SportingMatch />

      {/* DESTACADOS BENTO */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:px-12 md:py-28">
        <Reveal>
          <div className="mb-10 flex items-end justify-between gap-6">
            <div>
              <p className="eyebrow mb-3">Lo mejor de la semana</p>
              <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
                Artículos destacados
              </h2>
            </div>
            <Link to="/articulos" className="hidden items-center gap-2 font-display text-base font-bold uppercase tracking-wider text-crimson hover:underline sm:flex">
              Ver todos <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-12">
          {bento[0] && (
            <Reveal className="md:col-span-8" delay={0}>
              <ArticleCard article={bento[0]} big />
            </Reveal>
          )}
          <div className="flex flex-col gap-5 md:col-span-4">
            {bento.slice(1).map((a, i) => (
              <Reveal key={a.slug} delay={0.1 + i * 0.1} className="flex-1">
                <ArticleCard article={a} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CRÓNICAS */}
      <section className="border-y border-white/10 bg-ink2 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-12">
          <Reveal>
            <div className="mb-10 flex items-end justify-between gap-6">
              <div>
                <p className="eyebrow mb-3">Desde El Molinón y fuera de casa</p>
                <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
                  Últimas crónicas
                </h2>
              </div>
              <Link to="/cronicas" className="hidden items-center gap-2 font-display text-base font-bold uppercase tracking-wider text-crimson hover:underline sm:flex">
                Ver todas <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2">
            {cronicas.slice(0, 4).map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.08}>
                <Link to={`/articulo/${c.slug}`} data-testid={`home-cronica-${c.slug}`} className="block">
                  <Scoreboard match={{ ...c, slug: c.slug }} />
                  <p className="mt-3 font-display text-xl font-bold uppercase text-white">{c.title}</p>
                </Link>
              </Reveal>
            ))}
            {cronicas.length === 0 && (
              <p className="text-slate-500">Todavía no hay crónicas. ¡Vuelve tras el próximo partido!</p>
            )}
          </div>
        </div>
      </section>

      {/* TIKTOK */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="pointer-events-none absolute -right-40 top-1/2 h-[500px] w-[500px] -translate-y-1/2 rounded-full bg-crimson/20 blur-[140px]" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 md:px-12 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow mb-3">El vídeo es el otro campo</p>
            <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
              Todo empieza en <span className="text-crimson">@ENOLFC</span>
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300 sm:text-lg">
              Reacciones en directo, dimes y direcciones del Sporting y el humor que solo
              entiende quien sufre en El Molinón. Sígueme en TikTok y sé parte de la grada digital.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="https://www.tiktok.com/@enolfc"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tiktok-section-cta"
                className="flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-display text-lg font-bold uppercase tracking-wider text-ink transition-transform hover:scale-105"
              >
                <Play className="h-4 w-4 fill-current" /> Seguir en TikTok
              </a>
              <Link
                to="/sobre"
                className="flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-display text-lg font-bold uppercase tracking-wider text-white transition-colors hover:border-crimson hover:text-crimson"
              >
                Conocer ENOLFC <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            {tiktokUrls.length > 0 ? (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="tiktok-live-videos">
                {tiktokUrls.slice(0, 3).map((url) => (
                  <TikTokEmbed key={url} url={url} />
                ))}
                <TikTokEmbedLoader />
              </div>
            ) : (
            <div className="grid grid-cols-3 gap-4">
              {[
                "https://images.pexels.com/photos/3131405/pexels-photo-3131405.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
                "https://images.unsplash.com/photo-1602674809970-89073c530b0a?crop=entropy&cs=srgb&fm=jpg&q=85",
                "https://images.pexels.com/photos/41257/pexels-photo-41257.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
              ].map((src, i) => (
                <a
                  key={i}
                  href="https://www.tiktok.com/@enolfc"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`tiktok-video-card-${i}`}
                  className={`group relative overflow-hidden rounded-2xl border border-edge ${i === 1 ? "translate-y-6" : ""}`}
                >
                  <div className="aspect-[9/16]">
                    <img src={src} alt="Vídeo ENOLFC" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-ink/40 opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-crimson text-white">
                      <Play className="h-5 w-5 fill-current" />
                    </span>
                  </div>
                </a>
              ))}
            </div>
            )}
          </Reveal>
        </div>
      </section>

      {/* NEWSLETTER */}
      <NewsletterSection />

      {/* CTA CONTACTO */}
      <section className="mx-auto max-w-7xl px-5 pb-24 md:px-12">
        <Reveal>
          <div className="glass relative overflow-hidden rounded-3xl p-10 text-center md:p-16">
            <div className="stripes-rojiblancas absolute inset-x-0 top-0 h-1" />
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-extrabold uppercase leading-tight text-white sm:text-5xl">
              ¿Colaboramos? Prensa, marcas y rojiblanmos de corazón
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-300">
              Si quieres colaborar, entrevistarme o simplemente charlar de fútbol, escríbeme.
            </p>
            <Link
              to="/contacto"
              data-testid="home-cta-contacto"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-crimson px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-white transition-transform hover:scale-105"
            >
              Contactar ahora <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
